import React, { useMemo } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress } from "@mui/material";
import styled from "@emotion/styled";

const ScrollArea = styled(TableContainer)(({ theme }) => ({
    maxWidth: "100%",
    overflowX: "auto",
    borderRadius: theme.shape.borderRadius,
}));

const StickyNameCell = styled(TableCell)(({ theme }) => ({
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    minWidth: 200,
    borderRight: `1px solid ${theme.palette.divider}`,
}));

const StickyNameHeader = styled(StickyNameCell)(({ theme }) => ({
    zIndex: 4,
    backgroundColor: theme.palette.surface?.inset || theme.palette.background.default,
}));

const TotalCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== "$tone" })(({ theme, $tone }) => ({
    position: "sticky",
    right: 0,
    zIndex: 2,
    // The tint has to ride on an opaque base or rows scroll through this sticky column.
    backgroundColor: theme.palette.background.paper,
    ...($tone && {
        backgroundImage: `linear-gradient(${theme.palette[$tone].light}26, ${theme.palette[$tone].light}26)`,
        color: theme.palette[$tone].dark,
    }),
    borderLeft: `1px solid ${theme.palette.divider}`,
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: "right",
    minWidth: 90,
}));

const TotalHeader = styled(TotalCell)(({ theme }) => ({
    zIndex: 4,
    backgroundColor: theme.palette.surface?.inset || theme.palette.background.default,
}));

const DayHeader = styled(TableCell)(({ theme }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    minWidth: 76,
    backgroundColor: theme.palette.surface?.inset || theme.palette.background.default,
}));

const WeekendHeader = styled(DayHeader)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const HoursCell = styled(TableCell)(({ theme, $tone }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    ...($tone && {
        color: theme.palette[$tone].dark,
        backgroundColor: theme.palette[$tone].light + "26",
        fontWeight: theme.typography.fontWeightMedium,
    }),
}));

const EmptyCell = styled(HoursCell)(({ theme }) => ({
    color: theme.palette.text.disabled,
}));

const OffCell = styled(TableCell)(({ theme }) => ({
    textAlign: "center",
    whiteSpace: "nowrap",
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
    fontSize: theme.typography.size.xs,
    textTransform: "uppercase",
    letterSpacing: 0.5,
}));

const Legend = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LegendItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
}));

const LegendSwatch = styled(Box)(({ theme, $tone }) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: theme.palette[$tone].light + "26",
    border: `1px solid ${theme.palette[$tone].main}`,
}));

const OffSwatch = styled(Box)(({ theme }) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: theme.palette.action.disabledBackground,
    border: `1px solid ${theme.palette.divider}`,
}));

const DepartmentRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.surface?.inset || theme.palette.action.hover,
}));

const DepartmentCell = styled(StickyNameCell)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.surface?.inset || theme.palette.action.hover,
}));

const SubtotalRow = styled(TableRow)(({ theme }) => ({
    "& td": {
        fontWeight: theme.typography.fontWeightMedium,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const GrandTotalRow = styled(TableRow)(({ theme }) => ({
    "& td": {
        fontWeight: theme.typography.fontWeightBold,
        borderTop: `2px solid ${theme.palette.divider}`,
    },
}));

const EmployeeTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const StateBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
}));

const PLACEHOLDER = "—";

// Day keys are plain YYYY-MM-DD strings produced server-side; stepping them as UTC keeps the
// column list aligned with those buckets regardless of the viewer's own timezone.
export const buildDayColumns = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

    const days = [];
    const cursor = new Date(start);
    while (cursor <= end && days.length < 400) {
        const key = cursor.toISOString().slice(0, 10);
        const weekday = cursor.getUTCDay();
        days.push({
            key,
            label: cursor.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }),
            weekdayLabel: cursor.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
            isWeekend: weekday === 0 || weekday === 6,
        });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return days;
};

// Days nobody in the current selection logged against are dropped, which also keeps unworked
// future days in the range from rendering as empty columns.
export const visibleDayColumns = (startDate, endDate, rows) => {
    const all = buildDayColumns(startDate, endDate);
    if (!Array.isArray(rows) || rows.length === 0) return all;
    return all.filter((day) => rows.some((row) => (row.daily?.[day.key] || 0) > 0));
};

const formatHours = (value) => (value ? value.toFixed(2) : PLACEHOLDER);

// Tone is per employee: their own expected daily hours, falling back to the system default
// server-side. Only cells with time are toned — a blank day is a day off, not a shortfall.
const toneFor = (hours, expected) => {
    if (!hours || !expected) return null;
    const ratio = hours / expected;
    if (ratio >= 1) return "success";
    if (ratio >= 0.75) return "warning";
    return "error";
};

const localDayKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// Weekdays in the range already behind us. Today is excluded because a day in progress is not
// a shortfall — the same rule the resource utilisation widget applies.
export const elapsedWeekdays = (dayColumns) => {
    const todayKey = localDayKey(new Date());
    return (dayColumns || []).filter((day) => !day.isWeekend && day.key < todayKey).length;
};

// Approved leave is not a shortfall, so it comes out of that employee's own denominator. A
// leave day they logged time against still counts, matching how the day cells treat it.
export const workedWeekdaysForRow = (row, dayColumns, elapsed) => {
    const todayKey = localDayKey(new Date());
    const onLeave = (dayColumns || []).filter(
        (day) => !day.isWeekend && day.key < todayKey && row?.off?.[day.key] && !(row?.daily?.[day.key] > 0)
    ).length;
    return Math.max(0, elapsed - onLeave);
};

export const hasDailyTarget = (row, day) =>
    !day.isWeekend && !(row?.holidays || []).includes(day.key) && !row?.off?.[day.key];

const workingDays = (row, dayColumns) => (dayColumns || []).filter((day) => hasDailyTarget(row, day));

const pastWorkingDays = (row, dayColumns) => {
    const todayKey = localDayKey(new Date());
    return workingDays(row, dayColumns).filter((day) => day.key < todayKey);
};

export const backfillDayKeys = (row, dayColumns) =>
    pastWorkingDays(row, dayColumns)
        .filter((day) => !(row?.daily?.[day.key] > 0))
        .map((day) => day.key);

export const underTargetDayKeys = (row, dayColumns) => {
    const expected = row?.expectedDailyHours;
    if (!expected) return [];
    return pastWorkingDays(row, dayColumns)
        .filter((day) => {
            const hours = row?.daily?.[day.key] || 0;
            return hours > 0 && hours < expected;
        })
        .map((day) => day.key);
};

export const overTargetDayKeys = (row, dayColumns) => {
    const expected = row?.expectedDailyHours;
    if (!expected) return [];
    return workingDays(row, dayColumns)
        .filter((day) => (row?.daily?.[day.key] || 0) > expected)
        .map((day) => day.key);
};

// A row total is judged against the target it could have hit by now: the daily target times
// the weekdays that were actually available to them.
const toneForTotal = (total, expected, weekdays) => {
    if (!weekdays || !expected) return null;
    return toneFor(total / weekdays, expected);
};

const RunnitDailyHours = ({ data, loading, hasFetched, days, startDate, endDate }) => {
    const groups = useMemo(() => {
        const byDepartment = new Map();
        (data || []).forEach((row) => {
            const key = row.department || "Unassigned";
            if (!byDepartment.has(key)) byDepartment.set(key, []);
            byDepartment.get(key).push(row);
        });
        return [...byDepartment.entries()].map(([department, rows]) => ({
            department,
            rows,
            subtotals: days.map((day) => rows.reduce((sum, row) => sum + (row.daily?.[day.key] || 0), 0)),
            total: rows.reduce((sum, row) => sum + (row.total || 0), 0),
        }));
    }, [data, days]);

    // Every weekday in the requested range, not just the visible columns — days nobody logged
    // against are dropped from the table but still count against the target.
    const rangeDays = useMemo(() => buildDayColumns(startDate, endDate), [startDate, endDate]);
    const workedWeekdays = useMemo(() => elapsedWeekdays(rangeDays), [rangeDays]);

    const grandTotals = useMemo(
        () => ({
            perDay: days.map((day) => (data || []).reduce((sum, row) => sum + (row.daily?.[day.key] || 0), 0)),
            total: (data || []).reduce((sum, row) => sum + (row.total || 0), 0),
        }),
        [data, days]
    );

    if (loading) {
        return (
            <Paper>
                <StateBox>
                    <CircularProgress size={28} />
                    <Typography variant="body2">Generating daily hours…</Typography>
                </StateBox>
            </Paper>
        );
    }

    if (!hasFetched) {
        return (
            <Paper>
                <StateBox>
                    <Typography variant="body2">Pick a date range and select Generate Report to see daily hours per employee.</Typography>
                </StateBox>
            </Paper>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Paper>
                <StateBox>
                    <Typography variant="body2">No employees matched this report. Try a different department or date range.</Typography>
                </StateBox>
            </Paper>
        );
    }

    if (days.length === 0) {
        return (
            <Paper>
                <StateBox>
                    <Typography variant="body2">No time was logged on any day in this range. Try a wider range or a different department.</Typography>
                </StateBox>
            </Paper>
        );
    }

    return (
        <Paper>
            <Legend>
                <Typography variant="caption" color="text.secondary">Compared to each employee&apos;s daily target:</Typography>
                <LegendItem>
                    <LegendSwatch $tone="success" />
                    <Typography variant="caption" color="text.secondary">Met or exceeded</Typography>
                </LegendItem>
                <LegendItem>
                    <LegendSwatch $tone="warning" />
                    <Typography variant="caption" color="text.secondary">Within 25% under</Typography>
                </LegendItem>
                <LegendItem>
                    <LegendSwatch $tone="error" />
                    <Typography variant="caption" color="text.secondary">More than 25% under</Typography>
                </LegendItem>
                <LegendItem>
                    <OffSwatch />
                    <Typography variant="caption" color="text.secondary">Approved time off</Typography>
                </LegendItem>
                <Typography variant="caption" color="text.disabled">Days with no time logged by anyone shown are hidden.</Typography>
            </Legend>
            <ScrollArea>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StickyNameHeader>Employee</StickyNameHeader>
                            {days.map((day) => {
                                const Header = day.isWeekend ? WeekendHeader : DayHeader;
                                return (
                                    <Header key={day.key}>
                                        <Box>{day.weekdayLabel}</Box>
                                        <Box>{day.label}</Box>
                                    </Header>
                                );
                            })}
                            <TotalHeader>Total</TotalHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => (
                            <React.Fragment key={group.department}>
                                <DepartmentRow>
                                    <DepartmentCell colSpan={1}>{group.department}</DepartmentCell>
                                    <TableCell colSpan={days.length} />
                                    <TotalCell />
                                </DepartmentRow>

                                {group.rows.map((row) => (
                                    <TableRow key={row.userId} hover>
                                        <StickyNameCell>
                                            <Box>{row.employeeName}</Box>
                                            {row.title && <EmployeeTitle variant="caption">{row.title}</EmployeeTitle>}
                                        </StickyNameCell>
                                        {days.map((day) => {
                                            const hours = row.daily?.[day.key] || 0;
                                            const leave = row.off?.[day.key];
                                            // Leave only greys the cell out when nothing was logged; time booked on a
                                            // day off is still real work and stays measured against the target.
                                            if (!hours) {
                                                return leave ? (
                                                    <OffCell key={day.key} title={`${leave} — time off`}>Off</OffCell>
                                                ) : (
                                                    <EmptyCell key={day.key}>{PLACEHOLDER}</EmptyCell>
                                                );
                                            }
                                            const tip = leave ? `Target ${row.expectedDailyHours}h · logged during ${leave.toLowerCase()} leave` : `Target ${row.expectedDailyHours}h`;
                                            return (
                                                <HoursCell key={day.key} $tone={toneFor(hours, row.expectedDailyHours)} title={tip}>
                                                    {formatHours(hours)}
                                                </HoursCell>
                                            );
                                        })}
                                        {(() => {
                                            const available = workedWeekdaysForRow(row, rangeDays, workedWeekdays);
                                            const offDays = workedWeekdays - available;
                                            const tip = available
                                                ? `${formatHours((row.total || 0) / available)}h/day across ${available} weekday${available === 1 ? "" : "s"}`
                                                    + `${offDays ? ` (${offDays} on leave)` : ""} · target ${row.expectedDailyHours}h`
                                                : "No working days to measure against in this range";
                                            return (
                                                <TotalCell $tone={toneForTotal(row.total, row.expectedDailyHours, available)} title={tip}>
                                                    {formatHours(row.total)}
                                                </TotalCell>
                                            );
                                        })()}
                                    </TableRow>
                                ))}

                                <SubtotalRow>
                                    <StickyNameCell>{group.department} subtotal</StickyNameCell>
                                    {group.subtotals.map((value, index) => (
                                        <HoursCell key={days[index].key}>{formatHours(value)}</HoursCell>
                                    ))}
                                    <TotalCell>{formatHours(group.total)}</TotalCell>
                                </SubtotalRow>
                            </React.Fragment>
                        ))}

                        <GrandTotalRow>
                            <StickyNameCell>Grand total</StickyNameCell>
                            {grandTotals.perDay.map((value, index) => (
                                <HoursCell key={days[index].key}>{formatHours(value)}</HoursCell>
                            ))}
                            <TotalCell>{formatHours(grandTotals.total)}</TotalCell>
                        </GrandTotalRow>
                    </TableBody>
                </Table>
            </ScrollArea>
        </Paper>
    );
};

export default RunnitDailyHours;
